import React from 'react';
import { Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import MyProfile from './pages/MyProfile';
import Reports from './pages/Reports';
import ReportViewEnhanced from './pages/ReportViewEnhanced';
import About from './pages/About';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminReports from './pages/admin/AdminReports';
import AdminPrompts from './pages/admin/AdminPrompts';
import AdminPersonas from './pages/admin/AdminPersonas';
import AdminLLM from './pages/admin/AdminLLM';
import AdminLLMPerformance from './pages/admin/AdminLLMPerformance';
import AdminLLMOptimization from './pages/admin/AdminLLMOptimization';
import AdminAuthHealth from './pages/admin/AdminAuthHealth';
import AdminJobMatchQuality from './pages/admin/AdminJobMatchQuality';
import AdminFeedback from './pages/admin/AdminFeedback';
import DiagnosticTest from './pages/DiagnosticTest';
import SkillsTracker from './pages/SkillsTracker';
import AICoach from './pages/AICoach';
import JobMatchAI from './pages/JobMatchAI';
import Subscription from './pages/Subscription';
import Affiliates from './pages/Affiliates';
import CoursePage from './pages/CoursePage';
import UserGuidePage from './pages/UserGuidePage';
import PricingPage from './pages/PricingPage';
import ThankYouPage from './pages/ThankYouPage';
import NotFound from './pages/NotFound';
import RouteDebugger from './components/RouteDebugger';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider } from './contexts/AuthContext';
import { DegradedModeProvider } from './contexts/DegradedModeContext';
import { AICoachProvider } from './contexts/AICoachContext';
import { Toaster } from '@shared/components/ui/Toaster';

function App() {
  return (
    <AuthProvider>
      <AICoachProvider>
        <DegradedModeProvider>
          <RouteDebugger />
        <Routes>
        {/* Public routes */}
        <Route path="/" element={<Layout />}>
          <Route index element={<LandingPage />} />
          <Route path="about" element={<About />} />
          <Route path="affiliates" element={<Affiliates />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="terms-of-service" element={<TermsOfService />} />
          <Route path="cookie-policy" element={<CookiePolicy />} />
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="pricing" element={<PricingPage />} />

          {/* Protected routes */}
          <Route
            path="dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="profile"
            element={
              <PrivateRoute>
                <ErrorBoundary>
                  <MyProfile />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          <Route
            path="reports"
            element={
              <PrivateRoute>
                <Reports />
              </PrivateRoute>
            }
          />
          <Route
            path="reports/:id"
            element={
              <PrivateRoute>
                <ReportViewEnhanced />
              </PrivateRoute>
            }
          />
          <Route
            path="skills-tracker"
            element={
              <PrivateRoute>
                <SkillsTracker />
              </PrivateRoute>
            }
          />
          <Route
            path="ai-coach"
            element={
              <PrivateRoute>
                <AICoach />
              </PrivateRoute>
            }
          />
          <Route
            path="job-match"
            element={
              <PrivateRoute>
                <JobMatchAI />
              </PrivateRoute>
            }
          />
          <Route
            path="subscription"
            element={
              <PrivateRoute>
                <Subscription />
              </PrivateRoute>
            }
          />
          <Route
            path="thank-you"
            element={
              <PrivateRoute>
                <ThankYouPage />
              </PrivateRoute>
            }
          />
          <Route
            path="course"
            element={
              <PrivateRoute>
                <CoursePage />
              </PrivateRoute>
            }
          />
          <Route
            path="user-guide"
            element={
              <PrivateRoute>
                <UserGuidePage />
              </PrivateRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="admin/users"
            element={
              <AdminRoute>
                <AdminUsers />
              </AdminRoute>
            }
          />
          <Route
            path="admin/reports"
            element={
              <AdminRoute>
                <AdminReports />
              </AdminRoute>
            }
          />
          <Route
            path="admin/prompts"
            element={
              <AdminRoute>
                <AdminPrompts />
              </AdminRoute>
            }
          />
          <Route
            path="admin/personas"
            element={
              <AdminRoute>
                <AdminPersonas />
              </AdminRoute>
            }
          />
          <Route
            path="admin/llm"
            element={
              <AdminRoute>
                <AdminLLM />
              </AdminRoute>
            }
          />
          <Route
            path="admin/job-match-quality"
            element={
              <AdminRoute>
                <AdminJobMatchQuality />
              </AdminRoute>
            }
          />
          <Route
            path="admin/llm/performance"
            element={
              <AdminRoute>
                <AdminLLMPerformance />
              </AdminRoute>
            }
          />
          <Route
            path="admin/llm/optimization"
            element={
              <AdminRoute>
                <AdminLLMOptimization />
              </AdminRoute>
            }
          />
          <Route
            path="admin/auth-health"
            element={
              <AdminRoute>
                <AdminAuthHealth />
              </AdminRoute>
            }
          />
          <Route
            path="admin/feedback"
            element={
              <AdminRoute>
                <AdminFeedback />
              </AdminRoute>
            }
          />
          <Route
            path="admin/diagnostics"
            element={
              <AdminRoute>
                <DiagnosticTest />
              </AdminRoute>
            }
          />

          {/* Catch all route - show 404 page instead of auto-redirect */}
          <Route path="*" element={<NotFound />} />
        </Route>
        </Routes>
        <Toaster />
        </DegradedModeProvider>
      </AICoachProvider>
    </AuthProvider>
  );
}

export default App;
